import { Logger } from "neo-common";
import { GsapEffects } from "./GsapEffects";
import { TweenLite, TimelineLite } from "gsap";
import roundColor = PIXI.CanvasTinter.roundColor;

/**
 * Interface for alpha tween arguments
 */
export interface IAlphaTweenOptions {
    alphas: Array<number>;
    tweenEase?: string;
    tweenDuration?: number;
    tweenDelay?: number;
    tweenLoop?: boolean;
    paused?: boolean;

    callbackType?: string;
    callbackFunction?: (...params: any[]) => any;
    callbackParams?: any[];
    callbackScope?: Object;
}

/**
 * Interface for rotation tween arguments
 */
export interface IRotationTweenOptions {
    degrees: Array<number>;
    tweenEase?: string;
    tweenDuration?: number;
    tweenDelay?: number;
    tweenLoop?: boolean;
    paused?: boolean;

    callbackType?: string;
    callbackFunction?: (...params: any[]) => any;
    callbackParams?: any[];
    callbackScope?: Object;
}

/**
 * Interface for scale tween arguments
 *
 * todo Implement increment/decrement/... for tween values. E.g.: {'x': '+1.5'}
 */
export interface IScaleTweenOptions {
    tweenValues: Array<PIXI.Point>;
    tweenEase?: string;
    tweenDuration?: number;
    tweenDelay?: number;
    tweenLoop?: boolean;
    paused?: boolean;

    callbackType?: string;
    callbackFunction?: (...params: any[]) => any;
    callbackParams?: any[];
    callbackScope?: Object;
}

/**
 * Interface for position tween arguments
 */
export interface IPositionTweenOptions {
    tweenX: Array<number>;
    tweenY: Array<number>;
    tweenEase?: string;
    tweenDuration?: number;
    tweenDelay?: number;
    tweenLoop?: boolean;
    bezier?: Object;
    paused?: boolean;

    callbackType?: string;
    callbackFunction?: (...params: any[]) => any;
    callbackParams?: any[];
    callbackScope?: Object;
}

/**
 * Interface for tween arguments
 */
export interface ITweenOptions {
    tween: string;
    tweenValues: Array<number>;
    tweenEase?: Array<string>;
    tweenDuration?: Array<number>;
    tweenDelay?: Array<number>;
    tweenLoop?: boolean;
    bezier?: Object;
    paused?: boolean;

    callbackType?: string;
    callbackFunction?: (...params: any[]) => any;
    callbackParams?: any[];
    callbackScope?: Object;
}

export interface ITweenable {
    uid: string;
    name?: string;
    position: PIXI.Point | PIXI.ObservablePoint;
    scale: PIXI.Point | PIXI.ObservablePoint;
    alpha: number;
    rotation: number;
    skew: PIXI.ObservablePoint;
}

/**
 *  Tweening elements
 *
 *  todo Remove return types of tween methods (Use void) ? Or return type Tweens for chaining methods?
 *  todo Add timestamp of start into tween?
 *  todo Add check for specific properties of objects?
 *
 *  Example:
 *      Tweens.alphaTween(object, {alphas: alphaTween, tweenEase: ease, tweenDuration: tweenDuration, tweenDelay: tweenDelay});
 *      Tweens.rotationTween(object, {degrees: rotateTweenDegrees, tweenEase: ease, tweenDuration: tweenDuration, tweenDelay: tweenDelay});
 *      Tweens.scaleTween(object, {tweenX: scaleTweenX, tweenY: scaleTweenY, tweenEase: ease, tweenDuration: tweenDuration});
 *
 *  @class Tweens
 */
export class Tweens {
    private static RemoveAllTweensExclude: Array<String> = ["currentValue", "_frame"];
    static tweenArray: Array<TimelineLite> | Array<TweenLite> = [];
    static uid: string;

    /**
     * Record tween of object. Used for removing tweens applied on object.
     *
     * @param uid
     * @param tween
     */
    static registerTween(uid: string, tween: any): void {
        if (uid != null) {
            if (Tweens.tweenArray[uid] != null) {
                Tweens.tweenArray[uid].push(tween);
            } else {
                Tweens.tweenArray[uid] = [tween];
            }
        }
    }

    /**
     *
     * @returns {any}
     * @param uid
     */
    static getRegisteredTweens(uid?: string): any {
        // Array<createjs.Tween> | Array<TimelineLite> | Array<TweenLite>
        if (uid != null) {
            if (Tweens.tweenArray[uid] != null) {
                return {
                    tweens: Tweens.tweenArray[uid],
                    length: Tweens.tweenArray[uid].length,
                };
            }
            return null;
        } else {
            return {
                tweens: Tweens.tweenArray,
                length: Object.keys(Tweens.tweenArray).length,
            };
        }
    }

    /**
     * Only remove tween from registry
     * @param uid
     * @param property
     */
    static removeTweenFromRegistry(uid: string, property: string = "all"): any {
        if (uid != null) {
            if (Tweens.tweenArray[uid] != null) {
                for (let i: number = Tweens.tweenArray[uid].length - 1; i >= 0; i--) {
                    let tween: any = Tweens.tweenArray[uid][i];

                    if (tween.name === "alphaTween" && (property === "alpha" || property === "all")) {
                        Tweens.tweenArray[uid][i] = null;
                        Tweens.tweenArray[uid].splice(i, 1);
                    }
                    if (tween.name === "positionTween" && (property === "position" || property === "all")) {
                        Tweens.tweenArray[uid][i] = null;
                        Tweens.tweenArray[uid].splice(i, 1);
                    }
                    if (tween.name === "scaleTween" && (property === "scale" || property === "all")) {
                        Tweens.tweenArray[uid][i] = null;
                        Tweens.tweenArray[uid].splice(i, 1);
                    }
                    if (tween.name === "rotationTween" && (property === "rotation" || property === "all")) {
                        Tweens.tweenArray[uid][i] = null;
                        Tweens.tweenArray[uid].splice(i, 1);
                    }
                    if (
                        tween.name === property + "Tween" &&
                        property !== "alpha" &&
                        property !== "position" &&
                        property !== "scale" &&
                        property !== "rotation"
                    ) {
                        Tweens.tweenArray[uid][i] = null;
                        Tweens.tweenArray[uid].splice(i, 1);
                    }
                }
            }
        }
    }

    // TODO add comment
    static restartTweens(uid: string): any {
        if (Tweens.tweenArray[uid] != null) {
            for (let i: number = 0; i < Tweens.tweenArray[uid].length; i++) {
                Tweens.tweenArray[uid][i].restart();
            }
        }
    }

    /**
     * Removes tweens of object.
     *
     * Kills tween of object when property is specified.
     * If no property is specified, all tweens of object will be killed.
     *
     * @param object
     * @param property
     */
    static removeTweens<T extends ITweenable>(object: T, property: string = "all"): any {
        if (object !== undefined) {
            if (object.uid != null) {
                if (Tweens.tweenArray[object.uid] != null) {
                    if (Tweens.tweenArray[object.uid].length > 0) {
                        for (let i: number = Tweens.tweenArray[object.uid].length - 1; i >= 0; i--) {
                            let tween: any = Tweens.tweenArray[object.uid][i];

                            if (tween.name === "alphaTween" && (property === "alpha" || property === "all")) {
                                tween.kill({ alpha: true }, object);

                                Tweens.tweenArray[object.uid][i] = null;
                                Tweens.tweenArray[object.uid].splice(i, 1);
                            } else if (
                                tween.name === "positionTween" &&
                                (property === "position" || property === "all")
                            ) {
                                tween.kill(null, object.position);

                                Tweens.tweenArray[object.uid][i] = null;
                                Tweens.tweenArray[object.uid].splice(i, 1);
                            } else if (tween.name === "scaleTween" && (property === "scale" || property === "all")) {
                                tween.kill(null, object.scale);

                                Tweens.tweenArray[object.uid][i] = null;
                                Tweens.tweenArray[object.uid].splice(i, 1);
                            } else if (
                                tween.name === "rotationTween" &&
                                (property === "rotation" || property === "all")
                            ) {
                                tween.kill({ rotation: true }, object);

                                Tweens.tweenArray[object.uid][i] = null;
                                Tweens.tweenArray[object.uid].splice(i, 1);
                            } else if (tween.name === "skewTween" && (property === "skew" || property === "all")) {
                                tween.kill(null, object.skew);

                                Tweens.tweenArray[object.uid][i] = null;
                                Tweens.tweenArray[object.uid].splice(i, 1);
                            } else if (tween.name === property + "Tween" || property === "all") {
                                let customProperty = property;

                                if (
                                    property == "all" &&
                                    Tweens.RemoveAllTweensExclude.indexOf(tween.name.slice(0, -5)) > -1
                                ) {
                                    continue;
                                }

                                if (customProperty == "all") {
                                    customProperty = tween.name.replace("Tween", "");
                                }

                                tween.kill({ [customProperty]: true }, object);

                                Tweens.tweenArray[object.uid][i] = null;
                                Tweens.tweenArray[object.uid].splice(i, 1);
                            }
                        }
                    }
                }
            }
        }
    }

    /**
     * Applies alpha tween on object.
     *
     * @param object
     * @param options
     */
    static alphaTween<T extends ITweenable>(object: T, options: IAlphaTweenOptions): any {
        if (options.alphas.length < 2) {
            return;
        }

        object.alpha = options.alphas.shift();

        let tween: any;
        if (options.alphas.length === 1) {
            tween = GsapEffects.alphaTween_Tweenlite(object, options);
        } else if (options.alphas.length > 1) {
            tween = GsapEffects.alphaTween_TimelineLite(object, options);
        }

        tween.name = "alphaTween";
        tween.objectName = object.name;
        this.registerTween(object.uid, tween);

        return tween;
    }

    /**
     * Applies position tween on object.
     * Number of positions points X/Y is variable. Count of X's and Y's must be equal.
     *
     * todo Add check if object is not null
     * @param object: ImageElement | AnimationElement | BaseElement
     * @param options: PositionTweenOptions
     */
    static positionTween<T extends ITweenable>(object: T, options: IPositionTweenOptions): any {
        /* Todo Implement ability have only one position option. X or Y. */
        if ((!options.tweenX && !options.tweenY) || (options.tweenX.length < 2 && options.tweenY.length < 2)) {
            if (DEBUG) {
                this.log("ERROR No tweenX or tweenY option for " + (object.name ? object.name : "object"));
            }
            return;
        }

        // if values for one of the axes are not specified or in valid length, we use default axes value
        if (options.tweenX.length !== options.tweenY.length) {
            if (options.tweenX.length > options.tweenY.length) {
                options.tweenY = Array.apply(null, Array(options.tweenX.length)).map(
                    (value: any) => object.position.y || 0,
                );
            } else {
                options.tweenX = Array.apply(null, Array(options.tweenY.length)).map(
                    (value: any) => object.position.x || 0,
                );
            }
        }

        object.position.x = options.tweenX.shift();
        object.position.y = options.tweenY.shift();

        let tween: any;
        if (options.tweenX.length === 1) {
            tween = GsapEffects.positionTween_Tweenlite(object, options);
        } else if (options.tweenX.length > 1) {
            tween = GsapEffects.positionTween_TimelineLite(object, options);
        }

        tween.name = "positionTween";
        tween.objectName = object.name;
        this.registerTween(object.uid, tween);

        return tween;
    }

    /**
     * Applies rotation tween on object.
     * Number of degrees is variable.
     *
     * @param object
     * @param options
     */
    // TODO marek bug
    static rotationTween(object: ITweenable, options: IRotationTweenOptions): any {
        if (options.degrees.length < 2) {
            return;
        }

        object.rotation = Tweens.degrees2radians(options.degrees.shift());

        let tween: any;
        if (options.degrees.length === 1) {
            tween = GsapEffects.rotationTween_Tweenlite(object, options);
        } else if (options.degrees.length > 1) {
            tween = GsapEffects.rotationTween_TimelineLite(object, options);
        }

        tween.name = "rotationTween";
        tween.objectName = object.name;

        this.registerTween(object.uid, tween);

        return tween;
    }

    /**
     * Applies scale tween on object.
     * Number of scale points X/Y is variable. Count of X's and Y's must be equal.
     *
     * @param object: ImageElement | AnimationElement | BaseElement
     * @param options: ScaleTweenOptions
     */
    static scaleTween<T extends ITweenable>(object: T, options: IScaleTweenOptions): any {
        if (options.tweenValues.length < 2) {
            if (DEBUG) {
                this.log(
                    "ERROR No ScaleTweenX or ScaleTweenY or ScaleTween option for " +
                        (object.name ? object.name : "object"),
                );
            }
            return;
        }

        object.scale.x = options.tweenValues[0].x;
        object.scale.y = options.tweenValues[0].y;
        options.tweenValues.shift();

        let tween: any;
        if (options.tweenValues.length === 1) {
            tween = GsapEffects.scaleTween_Tweenlite(object, options);
        } else if (options.tweenValues.length > 1) {
            tween = GsapEffects.scaleTween_TimelineLite(object, options);
        }

        tween.name = "scaleTween";
        tween.objectName = object.name;

        this.registerTween(object.uid, tween);

        return tween;
    }

    /**
     * Applies position tween on object.
     * Number of positions points X/Y is variable. Count of X's and Y's must be equal.
     *
     * todo Add check if object is not null
     * @param object: ImageElement | AnimationElement | BaseElement
     * @param options: PositionTweenOptions
     */
    static customTween<T extends ITweenable>(object: T, options: ITweenOptions): any {
        let property: Array<string> = options.tween.split(".");
        options = Tweens.prepareTweenData(options);

        if (property.length == 1) {
            object[property[0]] = options.tweenValues.shift();
        } else if (property.length == 2) {
            object[property[0]][property[1]] = options.tweenValues.shift();
        }

        let tween: any;
        if (options.tweenValues.length === 1) {
            tween = GsapEffects.tween_Tweenlite(object, options);
        } else if (options.tweenValues.length > 1) {
            tween = GsapEffects.tween_TimelineLite(object, options);
        }

        tween.name = property[0] + "Tween";
        tween.objectName = object.name;
        this.registerTween(object.uid, tween);

        return tween;
    }

    static prepareTweenData(data: ITweenOptions): ITweenOptions {
        let length = data.tweenValues.length - 1;

        data.tweenEase = Tweens.stringValueToFormatedArray(length, data.tweenEase, "linear");
        data.tweenDelay = Tweens.numberValueToFormatedArray(length, data.tweenDelay, 0);
        data.tweenDelay = data.tweenDelay.map(function (x: number) {
            return x * GsapEffects.TickTime;
        });

        if (data.tweenDuration != null) {
            if (data.tweenDuration.length == 1) {
                let partDuration = data.tweenDuration[0] / length;
                data.tweenDuration = Tweens.numberValueToFormatedArray(length, null, partDuration);
            } else {
                data.tweenDuration = Tweens.numberValueToFormatedArray(length, data.tweenDuration, 30);
            }
        } else {
            let defaultValue = Math.floor(Math.random() * 120 + 120);
            data.tweenDuration = Tweens.numberValueToFormatedArray(length, null, defaultValue);
        }
        data.tweenDuration = data.tweenDuration.map(function (x: number) {
            return x * GsapEffects.TickTime;
        });

        //we need to convert degrees to radians if we rotating
        if (data.tween == "rotation") {
            data.tweenValues = data.tweenValues.map(function (x: number) {
                return Tweens.degrees2radians(x);
            });
        }

        return data;
    }

    /**
     * Create array of specified length, filled with data values, or value, or default value
     *
     * @param {number} length
     * @param {Array<string> | string} data
     * @param {string} defaultValue
     * @returns {Array<string>}
     */
    static stringValueToFormatedArray(length: number, data: Array<string>, defaultValue: string): Array<string> {
        if (data != null) {
            if (Array.isArray(data)) {
                if (data.length == length) {
                    return data;
                }

                if (data.length < length) {
                    let singleData = data[0];

                    for (let i: number = data.length; i < length; i++) {
                        data.push(singleData);
                    }
                    return data;
                }
            } else {
                return Array.apply(null, Array(length)).map((value: any) => data);
            }
        } else {
            return Array.apply(null, Array(length)).map((value: any) => defaultValue);
        }

        return null;
    }

    /**
     * Create array of specified length, filled with data values, or value, or default value
     *
     * @param {number} length
     * @param {Array<string> | string} data
     * @param {string} defaultValue
     * @returns {Array<string>}
     */
    static numberValueToFormatedArray(length: number, data: Array<number>, defaultValue: number): Array<number> {
        if (data != null) {
            if (Array.isArray(data)) {
                if (data.length == length) {
                    return data;
                }

                if (data.length < length) {
                    let singleData = data[0];

                    for (let i: number = data.length; i < length; i++) {
                        data.push(singleData);
                    }
                    return data;
                }
            } else {
                return Array.apply(null, Array(length)).map((value: any) => data);
            }
        } else {
            return Array.apply(null, Array(length)).map((value: any) => defaultValue);
        }

        return null;
    }

    /**
     *
     * @param degrees
     * @returns {number}
     */
    static degrees2radians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    /**
     * @protected componentName - component type name.
     * @returns {string}
     */
    public static get componentName(): string {
        return "Tweens";
    }

    /**
     * Trace to browser console.
     * @method log
     * @param s string
     * @return void
     */
    protected static log(s: string): void {
        if (DEBUG) {
            let compName = Tweens.componentName;
            Logger.debugGroup(compName, s);
        }
    }
}

export const TweensAttributes = {
    TWEEN: "Tween",
    TWEEN_VALUES: "TweenValues",
    TWEEN_EASE: "TweenEase",
    TWEEN_DURATION: "TweenDuration",
    TWEEN_DELAY: "TweenDelay",
    TWEEN_LOOP: "TweenLoop",
    TWEEN_PROGRESS: "TweenProgress",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/Components/Effects/Tweens.ts
 **/